import React from "react";
import { BrandButton } from "../button";
import { CircularProgress } from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";

export function getDnsPropagationBarColor(readyToSend, score) {
  if (readyToSend && score >= 85) return "#28a745";
  if (score >= 70) return "#ffc107";
  if (score >= 40) return "#fd7e14";
  return "#dc3545";
}

export function dnsPropagationStatusEmoji(status) {
  switch (status) {
    case "READY":
      return "✅";
    case "MOSTLY_READY":
      return "⚠️";
    case "PARTIAL":
      return "🕒";
    default:
      return "❌";
  }
}

const checkNowSx = {
  "&:disabled": {
    backgroundColor: "#ccc",
  },
  textTransform: "none",
  borderRadius: "6px",
  padding: "6px 16px",
  fontSize: "0.875rem",
};

export function DnsPropagationCheckNowButton({ onClick, isChecking }) {
  return (
    <BrandButton
      primaryHover="light"
      variant="contained"
      onClick={onClick}
      disabled={isChecking}
      startIcon={isChecking ? <CircularProgress size={16} /> : <RefreshIcon />}
      sx={checkNowSx}
    >
      {isChecking ? "Checking..." : "Check Now"}
    </BrandButton>
  );
}

export function DnsPropagationScoreBar({ readyToSend, score }) {
  const pct = score || 0;
  const fillColor = getDnsPropagationBarColor(readyToSend, score);
  return (
    <div className="propagation-score">
      <div className="score-bar">
        <div
          className="score-fill"
          style={{
            width: `${pct}%`,
            backgroundColor: fillColor,
          }}
        />
      </div>
      <span className="score-text">{pct}% propagated</span>
    </div>
  );
}

export function DnsPropagationSummary({ readyToSend, score, recommendation }) {
  const msg = recommendation?.message || "Status unknown";
  const status = recommendation?.status;
  return (
    <div className="propagation-summary">
      <div
        className="overall-status"
        style={{ color: getDnsPropagationBarColor(readyToSend, score) }}
      >
        <span className="status-icon">{dnsPropagationStatusEmoji(status)}</span>
        <span className="status-text">{msg}</span>
      </div>
      <DnsPropagationScoreBar readyToSend={readyToSend} score={score} />
    </div>
  );
}

export function DnsRecordStatusGrid({
  spfStatus,
  dkimStatus,
  dmarcStatus,
  bimiStatus,
}) {
  const cell = (label, ok) => (
    <div key={label} className={`record-item ${ok ? "active" : "inactive"}`}>
      <span className="record-type">{label}</span>
      <span className="record-status">{ok ? "✅" : "❌"}</span>
    </div>
  );
  return (
    <div className="record-status-grid">
      {cell("SPF", spfStatus)}
      {cell("DKIM", dkimStatus)}
      {cell("DMARC", dmarcStatus)}
      {cell("BIMI", bimiStatus)}
    </div>
  );
}

export function DnsWaitRecommendation({ waitMinutes, nextCheckRecommended }) {
  return (
    <div className="wait-recommendation">
      <div className="recommendation-box">
        <h5>⏱️ Recommended Wait Time</h5>
        <p>
          Please wait approximately <strong>{waitMinutes} minutes</strong>{" "}
          before sending emails. DNS records are still propagating across
          different servers worldwide.
        </p>
        {nextCheckRecommended && (
          <p className="next-check">
            Next check recommended:{" "}
            <strong>
              {new Date(nextCheckRecommended).toLocaleTimeString()}
            </strong>
          </p>
        )}
      </div>
    </div>
  );
}

export function DnsReadyToSendBanner() {
  return (
    <div className="ready-to-send">
      <div className="success-box">
        <h5>🎉 Ready to Send!</h5>
        <p>
          Your domain is fully configured and DNS records are properly
          propagated. You can now send email campaigns with confidence.
        </p>
      </div>
    </div>
  );
}

export function DnsLastCheckedFooter({ lastCheckTime, timeSinceLastCheck }) {
  return (
    <div className="propagation-details">
      <small>
        {lastCheckTime && <>Last checked: {lastCheckTime.toLocaleString()}</>}
        {timeSinceLastCheck != null && timeSinceLastCheck !== "" && (
          <> ({timeSinceLastCheck} minutes ago)</>
        )}
      </small>
    </div>
  );
}
